import type { IntlShape } from 'react-intl';

import { agentTodoMessages } from './agentTodo.messages';
import { readAffectedTodoTitles } from './parseAgentTodoResult';
import type { AgentTodoItem, AgentTodoOperation } from './types';

const joinTitles = (intl: IntlShape, titles: string[], max = 3): string => {
  if (titles.length === 0) return '';
  const shown = titles.slice(0, max);
  const remainder = titles.length - shown.length;
  const base = intl.formatList(shown, { type: 'unit', style: 'short' });
  return remainder > 0
    ? intl.formatMessage(agentTodoMessages.titlesWithRemainder, {
        titles: base,
        count: remainder,
      })
    : base;
};

export const buildTodoSubtitle = (
  intl: IntlShape,
  {
    operation,
    input,
    items,
    banner,
  }: {
    operation: AgentTodoOperation;
    input: Record<string, unknown>;
    items: AgentTodoItem[];
    banner?: string;
  }
): string | undefined => {
  if (banner?.includes('All assigned steps are complete')) {
    return intl.formatMessage(agentTodoMessages.allAssignedStepsComplete);
  }

  if (banner === 'Todo list cleared.') {
    return intl.formatMessage(agentTodoMessages.todoListCleared);
  }

  if (banner === 'Todo list is empty.') {
    return intl.formatMessage(agentTodoMessages.todoListEmpty);
  }

  const affected = readAffectedTodoTitles(operation, input, items);

  switch (operation) {
    case 'complete':
      return affected.length > 0
        ? intl.formatMessage(agentTodoMessages.markedComplete, {
            titles: joinTitles(intl, affected),
          })
        : undefined;
    case 'delete':
      if (affected.length === 0) return undefined;
      return intl.formatMessage(agentTodoMessages.removedTodos, {
        count: affected.length,
      });
    case 'update':
      return affected.length > 0
        ? intl.formatMessage(agentTodoMessages.updated, {
            titles: joinTitles(intl, affected),
          })
        : undefined;
    case 'create':
      return affected.length > 0
        ? intl.formatMessage(agentTodoMessages.added, {
            titles: joinTitles(intl, affected),
          })
        : undefined;
    case 'set':
      return items.length === 0
        ? intl.formatMessage(agentTodoMessages.todoListCleared)
        : undefined;
    case 'get':
      return items.length === 0
        ? intl.formatMessage(agentTodoMessages.todoListEmpty)
        : undefined;
  }
};
